import React, { useMemo } from 'react';
import {
  ListOfVehicles,
  VehicleData,
  Image,
  CardContentWrapper,
  CardWrapper,
  ViewAllResults,
  NoResultsHeader,
  NameMileage,
  ImageWrapper,
  ClampedText,
} from './SidewideSearch.styles';
import { Card, Headline, Icon, Text } from '@oneaudi/unified-web-components';
import type { InventoryGroup } from '../../../types';
import { HOST_NAME } from '../../constants';
import CPOLogo from '../../assets/CpoBadge';

interface VehicleListProps {
  vehiclesData: InventoryGroup | undefined;
  type: 'NEW' | 'USED' | 'CPO';
  showPrice: boolean;
}

const VehicleList: React.FC<VehicleListProps> = ({ vehiclesData, type, showPrice }) => {
  const vehicles = vehiclesData?.vehicles;
  const totalCount = vehiclesData?.totalCount;
  const vlpLink = vehiclesData?.vlpLink;

  const vlpRedirectUrl = `${HOST_NAME}${vlpLink}`;
  const isUsedVehicle = type === 'CPO' || type === 'USED';
  const isCpoVehicles = type === 'CPO';
  const isOneResult = totalCount === 1;

  const hasTechnicalError = vehiclesData?.code && vehiclesData?.reason?.includes('error');

  const ErrorState = useMemo(() => {
    return (
      <NoResultsHeader>
        <Headline
          tag="h3"
          variant={{
            order: '3',
            type: 'headline',
            weight: 'normal',
          }}
        >
          Results couldn’t be loaded.
        </Headline>
        <Text>Try again, or browse all available options on our vehicle listing pages.</Text>
      </NoResultsHeader>
    );
  }, []);

  const vlpRedirectCta = useMemo(() => {
    return (
      <CardWrapper tabIndex={0} href={vlpRedirectUrl} vlpCard>
        <Card margin={[0, 0, 0, 0]}>
          <CardContentWrapper>
            <Icon name="traffic" size="l" />
            <VehicleData gap={4} vlpCard>
              <Text>New Vehicle</Text>
              <ViewAllResults>
                <Text>
                  View all {totalCount} {isOneResult ? 'result' : 'results'}
                </Text>
                <Icon name="forward" size="s" />
              </ViewAllResults>
            </VehicleData>
          </CardContentWrapper>
        </Card>
      </CardWrapper>
    );
  }, [isOneResult, totalCount, vlpRedirectUrl]);

  return (
    <ListOfVehicles>
      {isCpoVehicles && <CPOLogo type="CPO_USA" />}
      {hasTechnicalError && ErrorState}
      {vehicles?.map((vehicle) => {
        const price =
          vehicle?.carPrices?.find((toCheck) => toCheck.type === 'final')?.price?.formattedValue ??
          'Available Soon';
        const priceToRender = showPrice ? price : null;
        return (
          <CardWrapper key={vehicle.id} tabIndex={0} href={vehicle.weblink}>
            <Card margin={[0, 0, 0, 0]}>
              <CardContentWrapper>
                <ImageWrapper>
                  <Image src={vehicle.imageUrl} />
                </ImageWrapper>
                <VehicleData>
                  <NameMileage>
                    <Text>
                      <ClampedText>
                        {vehicle.modelYear} {vehicle.modelName}
                      </ClampedText>
                    </Text>
                    {isUsedVehicle && (
                      <Text variant={{ order: '2', style: 'normal' }} className="mileage">
                        {vehicle.odometerText}
                      </Text>
                    )}
                  </NameMileage>
                  <Text variant={{ order: '1', style: 'bold' }}>{priceToRender}</Text>
                </VehicleData>
              </CardContentWrapper>
            </Card>
          </CardWrapper>
        );
      })}
      {vlpRedirectCta}
    </ListOfVehicles>
  );
};

export default VehicleList;
